// @mui
import { Box, Divider, LinearProgress, Stack, Typography } from '@mui/material';
// redux
import { useDispatch, useSelector } from 'react-redux';
import {
  addRecipients,
  addConversation,
  addMessage,
  setCurrentConversation,
  markMessageAsSent,
  markMessageWithError,
  startLoading,
  stopLoading,
  addUsersToConversation,
} from '../../../redux/slices/chat';
import { markConversationAsRead } from 'src/redux/thunks/conversation';
import { selectCurrentChatData } from 'src/selectors/conversationsSelectors';
import ChatRoom from './ChatRoom';
import ChatMessageList from './ChatMessageList';
import ChatMessageInput from './ChatMessageInput';
import ChatHeader from './ChatHeader';
import useChat from '../hooks/useChat';
import { CHAT_MODES } from '../constants/ChatModes';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { createSituationChat } from 'src/redux/slices/situations';
import useActiveConversation from '../hooks/useActiveConversation';
import ErrorFallback from 'src/common/ErrorFallback/ErrorFallback';

export default function ChatWindow() {
  const {
    conversations,
    contacts,
    recipients,
    activeConversationId,
    isLoading,
    user,
  } = useSelector(selectCurrentChatData);
  const { sendMessage, addUsersToGroup, situation } = useChat(
    activeConversationId,
    user.id
  );
  const { conversation, loading, error, refetchConversation } =
    useActiveConversation(conversations, activeConversationId, user.id);
  const previousConversation = getPreviousConversation(
    conversations,
    recipients,
    situation
  );
  const [unsentMessages, setUnsentMessages] = useState([]);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const mode = activeConversationId ? CHAT_MODES.DETAIL : CHAT_MODES.COMPOSE;
  const showMessageInput = mode === CHAT_MODES.DETAIL || recipients.length > 0;
  const disabled =
    (mode === CHAT_MODES.COMPOSE && recipients.length === 0) ||
    isLoading ||
    conversation.participants === 1 ||
    conversation.finished;

  const handleMarkConversationAsRead = () => {
    dispatch(
      markConversationAsRead({
        conversationId: activeConversationId,
        userId: user.id,
      })
    );
  };

  // TODO: Mejorar este codigo

  const handleSendMessage = async (data) => {
    const existingConversationId =
      previousConversation?.id || activeConversationId;

    const newMessage = {
      ...(existingConversationId
        ? { conversationId: existingConversationId }
        : { usersId: recipients.map((recipient) => recipient.id) }),
      message: {
        type: data.type,
        body: data.message,
        ...(existingConversationId && { temporaryId: Date.now() }),
        ...(data?.contentType && { contentType: data.contentType }),
      },
      ...(situation &&
        !situation?.conversationId && {
          situationId: situation.id,
        }),
    };

    if (existingConversationId) {
      dispatch(
        addMessage({
          ...newMessage,
          currentUser: user.id,
          message: {
            ...newMessage.message,
            senderId: user.id,
            id: newMessage.message.temporaryId,
            temporary: true,
            ...(newMessage.message.type !== 'text' && {
              body: '',
            }),
          },
        })
      );
    } else {
      dispatch(startLoading());
    }

    try {
      const response = await sendMessage(newMessage);

      if (mode === CHAT_MODES.COMPOSE && !previousConversation) {
        dispatch(addConversation(response));
        if (situation) {
          dispatch(
            createSituationChat({
              situationId: situation.id,
              conversationId: response.conversationId,
            })
          );
        }
      } else {
        dispatch(markMessageAsSent(response));
      }

      if (mode === CHAT_MODES.COMPOSE) {
        dispatch(addRecipients([]));
        dispatch(setCurrentConversation(response.conversationId));
      }
    } catch (error) {
      if (newMessage.conversationId) {
        dispatch(
          markMessageWithError({
            conversationId: newMessage.conversationId,
            temporaryId: newMessage.message.temporaryId,
          })
        );
        setUnsentMessages((prevMessages) => [...prevMessages, newMessage]);
      } else {
        enqueueSnackbar({ variant: 'error', message: t('chat_error') });
      }
    } finally {
      if (!newMessage.conversationId) {
        dispatch(stopLoading());
      }
    }
  };

  const handleResendMessage = async (message) => {
    const failedMessage = unsentMessages.find(
      (failedMessage) => failedMessage.message.temporaryId === message.id
    );

    if (!failedMessage) return;

    try {
      const response = await sendMessage(failedMessage);
      dispatch(markMessageAsSent(response));
      setUnsentMessages((prevMessages) =>
        prevMessages.filter(
          (failedMessage) => failedMessage.message.temporaryId !== message.id
        )
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddUsersToGroup = async (users, conversationId) => {
    const usersIds = users.map((user) => user.id);

    try {
      const response = await addUsersToGroup(usersIds, conversationId);
      dispatch(addUsersToConversation(response));
      dispatch(addRecipients([]));
    } catch (error) {
      console.log(error);
      enqueueSnackbar({
        variant: 'error',
        message: t('chat.join_group_error'),
      });
    }
  };

  if (loading)
    return (
      <Box width="100%">
        <LinearProgress />
      </Box>
    );

  if (error) {
    return (
      <ErrorFallback
        onRetry={refetchConversation}
        sx={{
          width: '100%',
        }}
      />
    );
  }

  return (
    <Stack sx={{ flexGrow: 1, minWidth: '1px' }}>
      <ChatHeader
        key={activeConversationId} // esto es para resetear el estado de chatMode
        mode={mode}
        conversation={conversation}
        contacts={contacts}
        recipients={recipients}
        addUsersToGroup={handleAddUsersToGroup}
      />
      <Divider />
      {showMessageInput ? (
        <>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <ChatMessageList
              conversation={conversation}
              handleMarkConversationAsRead={handleMarkConversationAsRead}
              handleResendMessage={handleResendMessage}
            />
            {mode === CHAT_MODES.DETAIL && (
              <ChatRoom
                conversation={conversation}
                participants={conversation.participants}
              />
            )}
          </Box>
          {isLoading && <LinearProgress />}
          {!conversation?.finished && (
            <ChatMessageInput
              conversationId={activeConversationId}
              onSend={handleSendMessage}
              disabled={disabled}
            />
          )}
        </>
      ) : (
        <Typography align="center">{t('chat.empty_chat_message')}</Typography>
      )}
    </Stack>
  );
}

const getPreviousConversation = (conversations, recipients, situation) => {
  const userId = recipients[0]?.id;

  if (
    (situation && !situation?.conversationId) ||
    !userId ||
    recipients.length !== 1
  )
    return null;

  return conversations.find(
    (conversation) =>
      conversation.type === 'ONE_TO_ONE' &&
      conversation.participants.some((user) => user.id === userId) &&
      !conversation.situationId
  );
};
